import { useEffect, useRef } from 'react';
import { useAccessibility } from '@/contexts/AccessibilityContext';

/**
 * Hook: request a WakeLock on the screen as long as `active === true` and
 * the global `screenAwake` accessibility preference is ON.
 *
 * Silently no-ops on browsers without WakeLock API (older iOS Safari).
 * The lock is automatically released on tab visibility change and must be
 * re-requested when the user returns — we handle that too.
 */
export function useWakeLock(active) {
  const { screenAwake } = useAccessibility();
  const lockRef = useRef(null);

  useEffect(() => {
    const shouldLock = !!active && !!screenAwake;
    if (!shouldLock) {
      if (lockRef.current) {
        lockRef.current.release?.().catch(() => {});
        lockRef.current = null;
      }
      return;
    }

    if (!('wakeLock' in navigator)) return; // unsupported, give up silently

    let cancelled = false;
    const request = async () => {
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          lock.release?.().catch(() => {});
          return;
        }
        lockRef.current = lock;
        // iOS/Chromium can revoke the lock when tab goes to background;
        // re-acquire it on visibility return.
        lock.addEventListener?.('release', () => {
          lockRef.current = null;
        });
      } catch (_err) {
        // Permission denied or power-save mode — nothing we can do.
      }
    };
    request();

    const onVis = () => {
      if (document.visibilityState === 'visible' && !lockRef.current) {
        request();
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      if (lockRef.current) {
        lockRef.current.release?.().catch(() => {});
        lockRef.current = null;
      }
    };
  }, [active, screenAwake]);
}
